/**
 * Sentry Error Monitoring Integration
 * 
 * ENV CONFIG:
 * - VITE_SENTRY_ENABLED: 'true' to enable Sentry
 * - VITE_SENTRY_DSN: Sentry DSN URL
 * - VITE_SENTRY_ENVIRONMENT: 'production' | 'staging' | 'development'
 */

import * as Sentry from '@sentry/react';

const SENTRY_ENABLED = import.meta.env.VITE_SENTRY_ENABLED === 'true';
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development';

// Get release from build time or fallback
const RELEASE = import.meta.env.VITE_SENTRY_RELEASE || `off-plan-marketplace@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`;

export const initSentry = () => {
    if (!SENTRY_ENABLED) {
        console.log('[Sentry] Disabled via VITE_SENTRY_ENABLED');
        return;
    }
    
    if (!SENTRY_DSN) {
        console.warn('[Sentry] VITE_SENTRY_DSN not set. Sentry will not initialize.');
        return;
    }
    
    try {
        Sentry.init({
            dsn: SENTRY_DSN,
            environment: ENVIRONMENT,
            release: RELEASE,
            
            // Integrations
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: true,
                    blockAllMedia: true,
                }),
            ],
            
            // Performance Monitoring
            tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,
            
            // Session Replay
            replaysSessionSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            
            // Error filtering
            beforeSend(event, hint) {
                // Filter out known non-actionable errors
                const error = hint.originalException;
                if (error instanceof Error) {
                    // Ignore network errors that are not actionable
                    if (error.message.includes('Failed to fetch')) {
                        return null;
                    }
                    // Ignore user-caused rate limits 
                    if (error.message.includes('RATE_LIMITED')) {
                        return null;
                    }
                }
                return event;
            },
        });
        
        console.log('[Sentry] Initialized:', { dsn: SENTRY_DSN.slice(0, 30) + '...', environment: ENVIRONMENT });
    } catch (err) {
        console.error('[Sentry] Failed to initialize:', err);
    }
};

/**
 * Capture exception with context
 */
export const captureError = (error: Error, context?: Record<string, any>) => {
    if (SENTRY_ENABLED && SENTRY_DSN) {
        Sentry.captureException(error, {
            extra: context,
        });
    } else {
        console.error('[Sentry Debug] Would capture:', error, context);
    }
};

/**
 * Set user context for error tracking
 */
export const setUser = (user: { id: string; email?: string }) => {
    if (SENTRY_ENABLED && SENTRY_DSN) {
        Sentry.setUser(user);
    }
};

/**
 * Clear user context (on logout)
 */
export const clearUser = () => {
    if (SENTRY_ENABLED && SENTRY_DSN) {
        Sentry.setUser(null);
    }
};

// Re-export Sentry's ErrorBoundary for use
export const SentryErrorBoundary = Sentry.ErrorBoundary;
